import { Component, OnDestroy, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, finalize, first, map, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { getBase64 } from 'src/app/common/utils/getBase64';
import { CreateOrUpdateWorkerEmploymentHistoryRecordRequest } from 'src/app/contracts/requests/create-or-update-worker-employment-history-record-request';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { EmploymentHistoryRecordService } from 'src/app/data/employment-history-record.service';
import { WorkerService } from 'src/app/data/worker.service';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { EmploymentHistoryRecordDto } from 'src/app/models/dtos/employment-history-record-dto';
import { EmploymentHistoryEmploymentTypeDto } from 'src/app/models/dtos/employment-history-employment-type-dto';
import { FileDto } from 'src/app/models/dtos/file-dto';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { AlertType } from 'src/app/shared/components/alert/alert.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
  selector: 'app-employment-history-record-modal',
  templateUrl: './employment-history-record-modal.component.html',
  styleUrls: ['./employment-history-record-modal.component.scss'],
  standalone: false
})
export class EmploymentHistoryRecordModalComponent implements OnDestroy {
  data = inject<{
    workerId: number;
    employmentHistoryRecordId?: number;
  }>(MAT_DIALOG_DATA);
  private dictionaryService = inject(DictionaryService);
  private snackbarService = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private workerService = inject(WorkerService);
  private formBuilder = inject(UntypedFormBuilder);
  private dialogRef = inject<MatDialogRef<EmploymentHistoryRecordModalComponent>>(MatDialogRef);
  private employmentHistoryRecordService = inject(EmploymentHistoryRecordService);

  employmentHistoryRecordForm: UntypedFormGroup;

  readonly maxFileCount: number = 5;

  agreementTerminationMethods$: Observable<DictionaryItem[]> = this.dictionaryService.getAgreementTerminationMethods();
  employers$: Observable<DictionaryItem[]>;
  employmentHistoryEmploymentTypes$: Observable<EmploymentHistoryEmploymentTypeDto[]> = this.dictionaryService.getEmploymentHistoryEmploymentTypes();
  workingTimes$: Observable<DictionaryItem[]> = this.dictionaryService.getWorkingTimes();

  selectedEmploymentHistoryEmploymentType = new BehaviorSubject<EmploymentHistoryEmploymentTypeDto>(null);
  isError = new BehaviorSubject<boolean>(false);
  formTouched = new BehaviorSubject<boolean>(false);

  private files: FileDto[] = [];
  private isLoadingRecord = false;

  private readonly timeBetweenInput = 300;
  private readonly minimumInputLetters = 2;
  private readonly unsubscribe$ = new Subject<void>();

  protected employmentHistoryRecord: EmploymentHistoryRecordDto;

  isEmploymentContract$: Observable<boolean>;
  agreementTerminationOptions$: Observable<DictionaryItem[]>;

  alertType$ = combineLatest([this.isError, this.formTouched]).pipe(
    map(([isError, touched]) => (isError && touched ? AlertType.Error : AlertType.Warning)),
  );


  constructor() {
    this.buildEmploymentHistoryRecordFormGroup();
    this.initializeObservables();
    this.onEmployerChange();
    this.onEmploymentTypeChange();
    if (this.data.employmentHistoryRecordId) {
      this.fetchEmploymentHistoryRecordById(this.data.employmentHistoryRecordId);
    }
  }

  get isReadonly(): boolean {
    return this.employmentHistoryRecord?.WorkerAgreementId !== null && this.employmentHistoryRecord?.WorkerAgreementId !== undefined;
  }

  get employerControl(): UntypedFormControl {
    return this.employmentHistoryRecordForm.get('employer') as UntypedFormControl;
  }

  get employerId(): number {
    const value = this.employmentHistoryRecordForm.get('employer').value;
    return typeof value === 'string' || value.Id === 0 ? null : value.Id;
  }

  get employerName(): string {
    const value = this.employmentHistoryRecordForm.get('employer').value;
    return typeof value === 'string' ? value : value.Name;
  }

  get employmentDateFrom(): Date {
    return this.employmentHistoryRecordForm.get('employmentDateFrom').value;
  }

  get employmentDateTo(): Date {
    return this.employmentHistoryRecordForm.get('employmentDateTo').value;
  }

  get employmentHistoryEmploymentTypeControl(): UntypedFormControl {
    return this.employmentHistoryRecordForm?.get('employmentHistoryEmploymentType') as UntypedFormControl;
  }

  get workingTimeControl() {
    return this.employmentHistoryRecordForm.get('workingTime');
  }

  get terminationOptionControl(): UntypedFormControl {
    return this.employmentHistoryRecordForm.get('terminationOption') as UntypedFormControl;
  }

  get formValues(): any {
    return this.employmentHistoryRecordForm.value;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  onFilesChange(files: File[]): void {
    this.spinner.show();
    this.files = [];
    let counter = 0;

    files.forEach((file) => {
      getBase64(file, (base64) => {
        this.files.push({ OriginalName: file.name, FileContent: base64 });
        counter++;
      });
    });

    const interval = setInterval(() => {
      if (counter == files.length) {
        this.spinner.hide();
        this.isError.next(this.shouldErrorBeDisplayed(this.files, this.employmentHistoryRecord?.Files));
        clearInterval(interval);
      }
    }, 200);
  }

  submit() {
    if (this.employmentHistoryRecordForm.invalid || this.isError.value || this.isReadonly) {
      this.employmentHistoryRecordForm.markAllAsTouched();
      this.formTouched.next(true);
      return;
    }

    this.spinner.show();
    const payload = this.createRequest();

    if (this.data.employmentHistoryRecordId) {
      this.updateEmploymentHistoryRecord(this.data.workerId, this.data.employmentHistoryRecordId, payload);
    } else {
      this.createEmploymentHistoryRecord(this.data.workerId, payload);
    }
  }

  close() {
    this.dialogRef.close();
  }

  displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  private buildEmploymentHistoryRecordFormGroup() {
    this.employmentHistoryRecordForm = this.formBuilder.group({
      address: [null, Validators.maxLength(250)],
      employer: [null, Validators.required],
      employmentDateFrom: [null, Validators.required],
      employmentDateTo: [null, Validators.required],
      employmentHistoryEmploymentType: [null, Validators.required],
      workingTime: [null],
      terminationOption: [null],
    });
  }

  private patchEmploymentHistoryRecordFormGroupValue(record: any) {
    if (!record) {
      return;
    }

    this.employmentHistoryRecordForm.patchValue({
      address: record.Address,
      employer: { Id: record.EmployerId, Name: record.Employer },
      employmentDateFrom: record.EmploymentDateFrom,
      employmentDateTo: record.EmploymentDateTo,
      employmentHistoryEmploymentType: record.EmploymentHistoryEmploymentTypeId,
      workingTime: record.WorkingTimeId,
    });
  }

  private createRequest(): CreateOrUpdateWorkerEmploymentHistoryRecordRequest {
    const selectedType = this.selectedEmploymentHistoryEmploymentType.value;
    const mappedTypeId = selectedType?.EmploymentTypeId ?? null;
    const isEmploymentContract = this.isEmploymentContractType(mappedTypeId);

    return {
      Address: this.formValues.address,
      EmployerId: this.employerId,
      EmployerName: this.employerName,
      EmploymentDateFrom: this.formValues.employmentDateFrom,
      EmploymentDateTo: this.formValues.employmentDateTo,
      WorkingTimeId: this.formValues.workingTime,
      EmploymentHistoryEmploymentTypeId: this.formValues.employmentHistoryEmploymentType,
      AgreementTerminationMethodId: isEmploymentContract ? this.formValues.terminationOption : null,
      AgreementTerminationModeId: isEmploymentContract ? null : this.formValues.terminationOption,
      Files: this.files,
    };
  }

  private createEmploymentHistoryRecord(workerId: number, payload: CreateOrUpdateWorkerEmploymentHistoryRecordRequest) {
    this.workerService
      .createWorkerEmploymentHistoryRecord(workerId, payload)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((response) => {
        this.snackbarService.openSuccessSnackBar(
          response?.IsPendingVerification ? Messages.WorkerFilePendingVerification : Messages.SuccessfullyCreatedEmploymentHistoryRecord,
        );
        this.dialogRef.close(true);
      });
  }

  private updateEmploymentHistoryRecord(
    workerId: number,
    employmentHistoryRecordId: number,
    payload: CreateOrUpdateWorkerEmploymentHistoryRecordRequest,
  ) {
    this.workerService
      .updateWorkerEmploymentHistoryRecord(workerId, employmentHistoryRecordId, payload)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((response) => {
        this.snackbarService.openSuccessSnackBar(
          response?.IsPendingVerification ? Messages.WorkerFilePendingVerification : Messages.SuccessfullyUpdatedEmploymentHistoryRecord,
        );
        this.dialogRef.close(true);
      });
  }

  private initializeObservables() {
    this.isEmploymentContract$ = combineLatest([
      this.employmentHistoryEmploymentTypeControl.valueChanges.pipe(startWith(null)),
      this.employmentHistoryEmploymentTypes$,
    ]).pipe(
      map(([ehEmpTypeId, types]) => {
        const mappedTypeId = types.find((t) => t.Id === ehEmpTypeId)?.EmploymentTypeId ?? null;
        return this.isEmploymentContractType(mappedTypeId);
      }),
      shareReplay(1),
    );

    this.agreementTerminationOptions$ = this.isEmploymentContract$.pipe(
      switchMap((isEmploymentContract) => {
        const ehEmpTypeId = this.employmentHistoryEmploymentTypeControl.value;
        if (!ehEmpTypeId) return of([]);
        if (isEmploymentContract) return this.agreementTerminationMethods$;
        return this.employmentHistoryEmploymentTypes$.pipe(
          first(),
          switchMap(types => {
            const mappedTypeId = types.find((t) => t.Id === ehEmpTypeId)?.EmploymentTypeId ?? null;
            if (mappedTypeId == null) return of([]);
            return this.dictionaryService.getAgreementTerminationModes(undefined, mappedTypeId);
          }),
        );
      }),
      shareReplay(1),
    );
  }

  private onEmploymentTypeChange() {
    combineLatest([this.employmentHistoryEmploymentTypes$, this.isEmploymentContract$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([employmentHistoryTypes, isEmploymentContract]) => {
        const ehEmpTypeId = this.employmentHistoryEmploymentTypeControl.value;
        const selectedType = employmentHistoryTypes.find((t) => t.Id === ehEmpTypeId) ?? null;
        this.selectedEmploymentHistoryEmploymentType.next(selectedType);

        if (!this.isReadonly && !this.isLoadingRecord) {
          this.terminationOptionControl.setValue(null);
        }

        if (!isEmploymentContract) {
          this.workingTimeControl.setValue(null);
        }

        this.updateFormValidation(isEmploymentContract);

        if (selectedType) {
          const shouldErrorBeDisplayed = this.shouldErrorBeDisplayed(this.files, this.employmentHistoryRecord?.Files);
          this.isError.next(shouldErrorBeDisplayed);
          this.formTouched.next(false);
        }
      });
  }

  private updateFormValidation(isEmploymentContract: boolean) {
    this.workingTimeControl.setValidators(isEmploymentContract ? [Validators.required] : null);
    this.workingTimeControl.updateValueAndValidity();
    this.terminationOptionControl.setValidators(isEmploymentContract ? [Validators.required] : null);
    this.terminationOptionControl.updateValueAndValidity();
  }

  private onEmployerChange() {
    this.employers$ = this.employerControl.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      switchMap((value: string) => (value && value.length > this.minimumInputLetters ? this.dictionaryService.getEmployers(value) : of([]))),
    );
  }

  private shouldErrorBeDisplayed = (addedFiles: FileDto[], savedfiles: string[]) =>
    !addedFiles.length && !savedfiles?.length;

  private isEmploymentContractType(mappedTypeId: number | null): boolean {
    return (
      mappedTypeId === EmploymentType.EmploymentAgreement ||
      mappedTypeId === EmploymentType.TemporaryEmploymentAgreement
    );
  }

  private fetchEmploymentHistoryRecordById(employmentHistoryRecordId: number) {
    this.employmentHistoryRecordService
      .getEmploymentHistoryRecordById(employmentHistoryRecordId)
      .pipe(first())
      .subscribe(async (res: EmploymentHistoryRecordDto) => {
        this.employmentHistoryRecord = res;
        this.isLoadingRecord = true;
        this.patchEmploymentHistoryRecordFormGroupValue(res);

        const mappedEmploymentTypeId = res.EmploymentTypeId;
        const isEmploymentContract = this.isEmploymentContractType(mappedEmploymentTypeId);
        const terminationValue = this.isReadonly
          ? isEmploymentContract ? res.AgreementTerminationMethod : res.AgreementTerminationMode
          : isEmploymentContract ? res.AgreementTerminationMethodId : res.AgreementTerminationModeId;

        this.agreementTerminationOptions$
          .pipe(
            filter(options => options.length > 0),
            first(),
            takeUntil(this.unsubscribe$),
          )
          .subscribe(() => {
            this.terminationOptionControl.setValue(terminationValue ?? null);
            this.isLoadingRecord = false;
          });

        if (this.isReadonly) {
          this.employmentHistoryRecordForm.disable();
        }
      });
  }
}
